import {
  applyDisconnect,
  applyJoin,
  applyLeave,
  applySetRole,
  MAX_TITLE_LENGTH,
  type Participant,
  type Role,
} from '../seats.js';

export type RetroMode = 'anonymous' | 'visible';
export type RetroPhase = 'writing' | 'revealed';
export type ColumnId = 'wentWell' | 'toImprove' | 'actionItems';

export const REFLECTION_COLUMNS: readonly ColumnId[] = ['wentWell', 'toImprove'];

export function isReflectionColumn(column: ColumnId): boolean {
  return (REFLECTION_COLUMNS as readonly string[]).includes(column);
}

export const MAX_NOTE_LENGTH = 280;
export const MAX_NOTES = 300;

/** One line of a note, in its original author's words. A fresh note has exactly one. */
export interface NoteFragment {
  readonly authorId: string;
  readonly text: string;
}

export interface RetroNote {
  readonly id: string;
  readonly column: ColumnId;
  /** This note's own content — never grows in place; a merge appends via `absorbed` instead. */
  readonly fragments: readonly NoteFragment[];
  readonly createdAt: number;
  /**
   * Notes merged into this one, oldest first, each as a complete standalone
   * note snapshot (not just its fragments) — so `unmergeNote` can restore the
   * most recently absorbed note exactly as it looked the instant before it
   * was combined, including whatever it had itself already absorbed. Popping
   * the last entry undoes one merge; the note underneath is untouched.
   */
  readonly absorbed: readonly RetroNote[];
  readonly votes: readonly string[];
}

export interface RetroRoom {
  readonly kind: 'retro';
  readonly id: string;
  readonly title: string;
  readonly mode: RetroMode;
  readonly hostId: string | null;
  readonly participants: Readonly<Record<string, Participant>>;
  readonly phase: RetroPhase;
  readonly notes: readonly RetroNote[];
  readonly noteSeq: number;
  readonly revealedAt: number | null;
  readonly createdAt: number;
}

export type RetroCommand =
  | { readonly type: 'join'; readonly name: string; readonly role: Role }
  | { readonly type: 'leave' }
  | { readonly type: 'disconnect' }
  | { readonly type: 'setRole'; readonly role: Role }
  | { readonly type: 'setTitle'; readonly title: string }
  | { readonly type: 'addNote'; readonly column: ColumnId; readonly text: string }
  | { readonly type: 'editNote'; readonly noteId: string; readonly text: string }
  | { readonly type: 'removeNote'; readonly noteId: string }
  | { readonly type: 'reveal' }
  | { readonly type: 'unreveal' }
  | { readonly type: 'combineNotes'; readonly noteId: string; readonly intoNoteId: string }
  | { readonly type: 'unmergeNote'; readonly noteId: string }
  | { readonly type: 'voteNote'; readonly noteId: string }
  | { readonly type: 'unvoteNote'; readonly noteId: string }
  | { readonly type: 'setMode'; readonly mode: RetroMode }
  | { readonly type: 'transferHost'; readonly participantId: string }
  | { readonly type: 'removeParticipant'; readonly participantId: string };

export interface RetroCommandMeta {
  readonly actorId: string;
  readonly now: number;
}

export type RetroErrorCode =
  | 'invalid_name'
  | 'room_full'
  | 'not_in_room'
  | 'host_only'
  | 'invalid_title'
  | 'observers_cannot_write'
  | 'column_locked'
  | 'board_frozen'
  | 'board_full'
  | 'invalid_note'
  | 'note_not_found'
  | 'not_note_author'
  | 'already_revealed'
  | 'not_revealed'
  | 'empty_board'
  | 'not_reflection_column'
  | 'not_merged'
  | 'note_is_merged'
  | 'already_voted'
  | 'unknown_participant'
  | 'participant_away'
  | 'participant_connected'
  | 'cannot_remove_self';

export interface RetroCommandError {
  readonly code: RetroErrorCode;
  readonly message: string;
}

export type RetroCommandResult =
  | { readonly ok: true; readonly room: RetroRoom }
  | { readonly ok: false; readonly error: RetroCommandError };

const ERROR_MESSAGES: Record<RetroErrorCode, string> = {
  invalid_name: 'Enter a display name.',
  room_full: 'This retro already has as many people in it as it holds.',
  not_in_room: 'You are not in this retro.',
  host_only: 'Only the host can do that.',
  invalid_title: 'That title is too long.',
  observers_cannot_write: 'Observers watch — they do not write or vote.',
  column_locked: 'That column is not taking notes right now.',
  board_frozen: 'The board is out in the open now — it is the record, not a draft.',
  board_full: `This board holds ${MAX_NOTES} notes at most.`,
  invalid_note: `A note runs from one character to ${MAX_NOTE_LENGTH}.`,
  note_not_found: 'That note is not on the board.',
  not_note_author: 'That note belongs to somebody else.',
  already_revealed: 'This retro has already started.',
  not_revealed: 'The board has not been revealed yet.',
  empty_board: 'Nobody has written anything yet.',
  not_reflection_column: 'Action items are not voted on or merged.',
  not_merged: 'That note has never been merged with anything.',
  note_is_merged: 'A merged note can only be unmerged, not edited — split it apart first.',
  already_voted: 'You have already voted for that one.',
  unknown_participant: 'That person is not in this retro.',
  participant_away: 'They are away — hand the retro to someone who is here.',
  participant_connected: 'You can only clear a seat that has gone away.',
  cannot_remove_self: 'Leave the retro instead.',
};

function fail(code: RetroErrorCode): RetroCommandResult {
  return { ok: false, error: { code, message: ERROR_MESSAGES[code] } };
}

function succeed(room: RetroRoom): RetroCommandResult {
  return { ok: true, room };
}

export function createRetroRoom(options: { id: string; title: string; mode: RetroMode; now: number }): RetroRoom {
  const title = options.title.trim().slice(0, MAX_TITLE_LENGTH);
  return {
    kind: 'retro',
    id: options.id,
    title: title.length > 0 ? title : 'Retro',
    mode: options.mode,
    hostId: null,
    participants: {},
    phase: 'writing',
    notes: [],
    noteSeq: 0,
    revealedAt: null,
    createdAt: options.now,
  };
}

function noteText(raw: string): string | null {
  const text = raw.trim();
  if (text.length === 0 || text.length > MAX_NOTE_LENGTH) return null;
  return text;
}

function columnAccepts(room: RetroRoom, column: ColumnId): boolean {
  return isReflectionColumn(column) ? room.phase === 'writing' : room.phase !== 'writing';
}

/**
 * Drops every vote cast by `participantId`. A vote only ever counts while its caster is a
 * voting member of the room, and nothing else prunes `note.votes` later — so every path that
 * ends a participant's standing to vote (leaving, or turning observer) must run votes through
 * here first, or the stale vote inflates the count for the life of the board.
 */
function releaseVotes(room: RetroRoom, participantId: string): RetroRoom {
  const notes = room.notes.map((note) =>
    note.votes.includes(participantId) ? { ...note, votes: note.votes.filter((id) => id !== participantId) } : note,
  );
  return { ...room, notes };
}

export function applyRetroCommand(room: RetroRoom, command: RetroCommand, meta: RetroCommandMeta): RetroCommandResult {
  const { actorId, now } = meta;
  const actor = room.participants[actorId];

  if (command.type === 'join') {
    const result = applyJoin(room, actorId, command.name, command.role, now);
    return result.ok ? succeed(result.roster) : fail(result.error);
  }

  if (!actor) return fail('not_in_room');

  switch (command.type) {
    case 'leave':
      return succeed(applyLeave(releaseVotes(room, actorId), actorId));

    // A disconnect is not a departure: the seat is held open for a reconnect, so the votes stay.
    case 'disconnect':
      return succeed(applyDisconnect(room, actorId));

    case 'setRole': {
      if (actor.role === command.role) return succeed(room);
      const released = command.role === 'observer' ? releaseVotes(room, actorId) : room;
      return succeed(applySetRole(released, actorId, command.role));
    }

    case 'setTitle': {
      if (room.hostId !== actorId) return fail('host_only');
      const title = command.title.trim();
      if (title.length > MAX_TITLE_LENGTH) return fail('invalid_title');
      return succeed({ ...room, title });
    }

    // Unlike setDeck, there is nothing to clear: concealment is computed fresh
    // per view from `room.mode` and `note.authorId` (never stored on the note
    // itself), so flipping the field is the whole effect. Anonymous -> visible
    // surfaces every existing author on the next broadcast; visible ->
    // anonymous re-conceals them. Both are allowed at any phase — the host
    // is trusted with this the same way they are with the deck.
    case 'setMode': {
      if (room.hostId !== actorId) return fail('host_only');
      if (command.mode === room.mode) return succeed(room);
      return succeed({ ...room, mode: command.mode });
    }

    case 'addNote': {
      if (actor.role === 'observer') return fail('observers_cannot_write');
      if (!columnAccepts(room, command.column)) return fail('column_locked');
      if (room.notes.length >= MAX_NOTES) return fail('board_full');
      const text = noteText(command.text);
      if (text === null) return fail('invalid_note');
      const note: RetroNote = {
        id: `${room.id}-n${room.noteSeq}`,
        column: command.column,
        fragments: [{ authorId: actorId, text }],
        createdAt: now,
        absorbed: [],
        votes: [],
      };
      return succeed({ ...room, notes: [...room.notes, note], noteSeq: room.noteSeq + 1 });
    }

    // Only ever reaches a single-fragment, never-merged note: once merged, a note's
    // authorship spans more than one person and free-text editing could no longer be
    // attributed to any of them truthfully — see `note_is_merged`. `unmergeNote` is the
    // only way to change a merged note's content again.
    case 'editNote': {
      const note = room.notes.find((candidate) => candidate.id === command.noteId);
      if (!note) return fail('note_not_found');
      const fragment = note.fragments[0]!;
      if (note.absorbed.length > 0) return fail('note_is_merged');
      if (isReflectionColumn(note.column)) {
        if (room.phase !== 'writing') return fail('board_frozen');
        if (fragment.authorId !== actorId) return fail('not_note_author');
      } else if (fragment.authorId !== actorId && room.hostId !== actorId) {
        return fail('not_note_author');
      }
      const text = noteText(command.text);
      if (text === null) return fail('invalid_note');
      const notes = room.notes.map((candidate) =>
        candidate.id === note.id ? { ...candidate, fragments: [{ authorId: fragment.authorId, text }] } : candidate,
      );
      return succeed({ ...room, notes });
    }

    // A merged note has no single author to check permission against, so removing one
    // outright is a host power, the same as merging it was — see `combineNotes`. An
    // unmerged note keeps the original per-author permission.
    case 'removeNote': {
      const note = room.notes.find((candidate) => candidate.id === command.noteId);
      if (!note) return fail('note_not_found');
      if (note.absorbed.length > 0) {
        if (room.hostId !== actorId) return fail('host_only');
      } else {
        const fragment = note.fragments[0]!;
        if (isReflectionColumn(note.column)) {
          if (room.phase !== 'writing') return fail('board_frozen');
          if (fragment.authorId !== actorId) return fail('not_note_author');
        } else if (fragment.authorId !== actorId && room.hostId !== actorId) {
          return fail('not_note_author');
        }
      }
      return succeed({ ...room, notes: room.notes.filter((candidate) => candidate.id !== note.id) });
    }

    case 'reveal': {
      if (room.hostId !== actorId) return fail('host_only');
      if (room.phase !== 'writing') return fail('already_revealed');
      if (room.notes.length === 0) return fail('empty_board');
      return succeed({ ...room, phase: 'revealed', revealedAt: now });
    }

    // For "I forgot to add something." Deliberately does not clear anything:
    // notes, mergedIds and votes all stay exactly as they were, so an early
    // reveal is a correctable mistake rather than a restart. actionItems
    // re-locks and the reflection columns re-open for free, the same
    // `columnAccepts` phase check that opened/locked them on reveal.
    case 'unreveal': {
      if (room.hostId !== actorId) return fail('host_only');
      if (room.phase !== 'revealed') return fail('not_revealed');
      return succeed({ ...room, phase: 'writing', revealedAt: null });
    }

    case 'combineNotes': {
      if (room.hostId !== actorId) return fail('host_only');
      if (room.phase !== 'revealed') return fail('column_locked');
      if (command.noteId === command.intoNoteId) return succeed(room);
      const source = room.notes.find((candidate) => candidate.id === command.noteId);
      const target = room.notes.find((candidate) => candidate.id === command.intoNoteId);
      if (!source || !target) return fail('note_not_found');
      if (!isReflectionColumn(source.column) || source.column !== target.column) return fail('not_reflection_column');
      // `source` is absorbed whole, exactly as it stands right now, so a later `unmergeNote`
      // can hand it back unchanged — including any fragments or absorptions it already carried
      // from earlier merges of its own.
      const merged: RetroNote = {
        ...target,
        absorbed: [...target.absorbed, source],
        votes: [...new Set([...target.votes, ...source.votes])],
      };
      const notes = room.notes
        .filter((candidate) => candidate.id !== source.id)
        .map((candidate) => (candidate.id === target.id ? merged : candidate));
      return succeed({ ...room, notes });
    }

    // Peels the most recently merged note back off, restoring it exactly as it was the
    // instant before `combineNotes` absorbed it — same fragments, same votes, same id. The
    // note doing the peeling keeps every vote it currently has: there is no way to know
    // which voter meant which half, so nothing is discarded on the split either.
    case 'unmergeNote': {
      if (room.hostId !== actorId) return fail('host_only');
      const note = room.notes.find((candidate) => candidate.id === command.noteId);
      if (!note) return fail('note_not_found');
      if (note.absorbed.length === 0) return fail('not_merged');
      const restored = note.absorbed[note.absorbed.length - 1]!;
      const remaining: RetroNote = { ...note, absorbed: note.absorbed.slice(0, -1) };
      const notes = room.notes.map((candidate) => (candidate.id === note.id ? remaining : candidate)).concat(restored);
      return succeed({ ...room, notes });
    }

    case 'voteNote': {
      if (actor.role === 'observer') return fail('observers_cannot_write');
      if (room.phase !== 'revealed') return fail('column_locked');
      const note = room.notes.find((candidate) => candidate.id === command.noteId);
      if (!note) return fail('note_not_found');
      if (!isReflectionColumn(note.column)) return fail('not_reflection_column');
      if (note.votes.includes(actorId)) return fail('already_voted');
      const notes = room.notes.map((candidate) =>
        candidate.id === note.id ? { ...candidate, votes: [...candidate.votes, actorId] } : candidate,
      );
      return succeed({ ...room, notes });
    }

    // No observer guard here: an observer holding a vote from before the role change is exactly
    // what `releaseVotes` already cleared, and retracting is never the harmful direction anyway.
    case 'unvoteNote': {
      if (room.phase !== 'revealed') return fail('column_locked');
      const note = room.notes.find((candidate) => candidate.id === command.noteId);
      if (!note) return fail('note_not_found');
      if (!isReflectionColumn(note.column)) return fail('not_reflection_column');
      if (!note.votes.includes(actorId)) return succeed(room);
      const notes = room.notes.map((candidate) =>
        candidate.id === note.id ? { ...candidate, votes: candidate.votes.filter((id) => id !== actorId) } : candidate,
      );
      return succeed({ ...room, notes });
    }

    case 'transferHost': {
      if (room.hostId !== actorId) return fail('host_only');
      if (command.participantId === actorId) return succeed(room);
      const target = room.participants[command.participantId];
      if (!target) return fail('unknown_participant');
      if (!target.connected) return fail('participant_away');
      return succeed({ ...room, hostId: target.id });
    }

    case 'removeParticipant': {
      if (room.hostId !== actorId) return fail('host_only');
      if (command.participantId === actorId) return fail('cannot_remove_self');
      const target = room.participants[command.participantId];
      if (!target) return fail('unknown_participant');
      if (target.connected) return fail('participant_connected');
      return succeed(applyLeave(releaseVotes(room, target.id), target.id));
    }

    default: {
      const exhaustive: never = command;
      throw new Error(`unhandled retro command: ${JSON.stringify(exhaustive)}`);
    }
  }
}
